/**
 * @fileoverview Context for managing signup flow state
 * @description Provides shared state between signup steps
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import type { SignupData, SignupUser } from '../service/types'

/** Signup step type */
type SignupStep = 1 | 2 | 3

/** Initial signup data */
const initialData: SignupData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  grades: [],
  schoolId: undefined,
  newSchoolName: undefined,
  zipCode: undefined,
  googleToken: undefined,
}

/** Context value interface */
interface SignupContextType {
  /** Current signup data */
  data: SignupData
  /** Update signup data */
  setData: (updates: Partial<SignupData>) => void
  /** Current step */
  step: SignupStep
  /** Navigate to step */
  setStep: (step: SignupStep) => void
  /** Whether user authenticated with Google */
  isGoogleAuth: boolean
  /** Set Google auth status */
  setGoogleAuth: (token: string) => void
  /** Created user data from signup API */
  user: SignupUser | null
  /** Set created user data */
  setUser: (user: SignupUser) => void
  /** Reset entire context */
  reset: () => void
}

const SignupContext = createContext<SignupContextType | undefined>(undefined)

/**
 * Provider for signup flow state
 * @param children - Child components
 */
export const SignupProvider = ({ children }: { children: ReactNode }) => {
  const [data, setDataState] = useState<SignupData>(initialData)
  const [step, setStep] = useState<SignupStep>(1)
  const [user, setUserState] = useState<SignupUser | null>(null)

  const setData = useCallback((updates: Partial<SignupData>) => {
    setDataState((prev) => ({ ...prev, ...updates }))
  }, [])

  const setGoogleAuth = useCallback((token: string) => {
    setDataState((prev) => ({ ...prev, googleToken: token }))
  }, [])

  const setUser = useCallback((userData: SignupUser) => {
    setUserState(userData)
  }, [])

  const reset = useCallback(() => {
    setDataState(initialData)
    setUserState(null)
    setStep(1)
  }, [])

  const isGoogleAuth = Boolean(data.googleToken)

  const value = useMemo(
    () => ({ data, setData, step, setStep, isGoogleAuth, setGoogleAuth, user, setUser, reset }),
    [data, step, isGoogleAuth, user, setData, setGoogleAuth, setUser, reset]
  )

  return <SignupContext.Provider value={value}>{children}</SignupContext.Provider>
}

/**
 * Hook to access signup context
 * @throws Error if used outside SignupProvider
 */
export const useSignupContext = (): SignupContextType => {
  const context = useContext(SignupContext)
  if (!context) {
    throw new Error('useSignupContext must be used within SignupProvider')
  }
  return context
}
